import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  input
} from '@angular/core';
import { Router } from '@angular/router';
import { Hex } from 'viem';
import { cpuOpponentLabel, isCpuAddress } from '../../data/address-config';
import { MonMetadata } from '../../generated/mon';
import { AddressColorPipe } from '../../pipes/address-color.pipe';
import { MonStaticSpriteComponent } from '../mon-static-sprite/mon-static-sprite.component';
import { DevService } from '../../services/dev.service';
import { MobileScaleService } from '../../services/mobile-scale.service';
import { OnchainStateService } from '../../services/onchain-state.service';
import { ServerService } from '../../services/server.service';
import { shortenAddress } from '../../services/utils.service';
import { Wallet } from '../../services/wallet';
import { ChatMessage } from '../../types/chat';
import { NameRevealComponent } from '../name-reveal/name-reveal.component';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';

const SPECTATE_WINDOW_MS = 60 * 60 * 1000;

@Component({
  selector: 'app-lobby-feed-event',
  imports: [
    AddressColorPipe,
    DatePipe,
    NameRevealComponent,
    PixelCreatureComponent,
    MonStaticSpriteComponent,
  ],
  templateUrl: './lobby-feed-event.component.html',
  styleUrl: './lobby-feed-event.component.css',
  host: { '[class.inline]': 'inline()' },
})
export class LobbyFeedEventComponent {
  readonly msg = input.required<ChatMessage>();
  readonly completedBattleKeys = input.required<ReadonlySet<Hex>>();
  readonly nowMs = input.required<number>();
  /** One-line, chrome-free rendering for the deck header's ticker. Same copy, no card. */
  readonly inline = input(false);

  readonly dev = inject(DevService);
  readonly mobile = inject(MobileScaleService);
  readonly server = inject(ServerService);
  readonly state = inject(OnchainStateService);
  private readonly router = inject(Router);
  private readonly wallet = inject(Wallet);

  readonly showTimestamp = signal(false);

  readonly involvesMe = computed<boolean>(() => {
    const me = this.wallet.activeAddress()?.toLowerCase();
    if (!me) return false;
    const ev = this.msg().battleLogEvent;
    if (!ev) return false;
    switch (ev.type) {
      case 'BATTLE_START':
        return ev.p0.toLowerCase() === me || ev.p1.toLowerCase() === me;
      case 'BATTLE_COMPLETE':
        return ev.winner.toLowerCase() === me || ev.loser.toLowerCase() === me;
      case 'MON_LEVEL_UP':
        return ev.player.toLowerCase() === me;
      default:
        return false;
    }
  });

  /** The human seat of a finished boss attempt — the boss is always the CPU side. */
  readonly dailyOutcome = computed(() => {
    const ev = this.msg().battleLogEvent;
    if (!ev || ev.type !== 'BATTLE_COMPLETE' || ev.dailyDay === undefined) return null;
    return isCpuAddress(ev.winner)
      ? { address: ev.loser, username: ev.loserUsername, twitterHandle: ev.loserTwitterHandle, won: false }
      : { address: ev.winner, username: ev.winnerUsername, twitterHandle: ev.winnerTwitterHandle, won: true };
  });

  toggleTimestamp(): void {
    this.showTimestamp.update((v) => !v);
  }

  readonly shortHex = shortenAddress;

  displayFallback(addr: Hex): string {
    // Collapsed CPU: the address no longer encodes the tier — use the difficulty the event carried.
    if (isCpuAddress(addr)) {
      const ev = this.msg().battleLogEvent;
      const diff =
        ev && (ev.type === 'BATTLE_START' || ev.type === 'BATTLE_COMPLETE') ? ev.cpuDifficulty : undefined;
      return cpuOpponentLabel(diff);
    }
    return this.shortHex(addr);
  }

  monName(id: number): string {
    return MonMetadata[id as keyof typeof MonMetadata]?.name ?? `#${id}`;
  }

  modeLabel(mode: 'singles' | 'doubles' | 'multi' | undefined): string {
    // Battle-log events persisted before doubles/multi support carry no `mode` in their
    // stored payload; those battles were always singles.
    if (!mode) return 'Singles';
    return mode.charAt(0).toUpperCase() + mode.slice(1);
  }

  readonly canSpectate = computed<boolean>(() => {
    const ev = this.msg().battleLogEvent;
    if (!ev || ev.type !== 'BATTLE_START' || isCpuAddress(ev.p1)) return false;
    if (this.completedBattleKeys().has(ev.battleKey)) return false;
    return this.nowMs() - ev.timestamp < SPECTATE_WINDOW_MS;
  });

  onViewReplay(battleKey: Hex): void {
    this.router.navigate(['/battle', battleKey, 'replay']);
  }

  onSpectate(battleKey: Hex): void {
    this.router.navigate(['/battle', battleKey]);
  }
}
